import React, {type Dispatch,
    type SetStateAction,
    useCallback,
} from 'react';


import {DateInput} from 'src/components/input';
import {RequiredMark} from 'src/components/required-mark';
import {ServerResponseHandler} from 'src/components/server-response-handler';
import {Skeleton} from 'src/components/skeleton';
import {WidthItem} from 'src/components/width-item';
import {ValidatedControl} from 'src/contexts/validation-context';
import {ACCESS_RIGHTS} from 'src/hooks/access-rights';
import {ResetPeriod as ResetPeriods} from 'src/openapi/admin/models/ResetPeriod';

import {CheckBox} from '@pb/kit-component-checkbox';
import ComboBox, {ComboboxItem} from '@pb/kit-component-combo-box';
import {Group} from '@pb/kit-component-group';
import Label from '@pb/kit-component-label';

import {NEXT_RESET_DATE_CONTROL} from './constants';
import {UserResetSettingsSkeleton} from './user-reset-settings.skeleton';

import type {AccessRight} from 'src/hooks/access-rights/constants';
import type {Error} from 'src/openapi/admin/models/Error';
import type {MicroSiteUserSettings} from 'src/openapi/admin/models/MicroSiteUserSettings';
import type {ResetPeriod} from 'src/openapi/admin/models/ResetPeriod';
import type {CheckBoxChangeData} from 'src/typedef';

type Props = {
    isLoading: boolean,
    loadingError: Error | null,
    data: MicroSiteUserSettings,
    setData: Dispatch<SetStateAction<MicroSiteUserSettings>>,
    trackChange: (parameter: string) => void,
    readonly?: boolean,
};

export const UserResetSettings = (props: Props) => {
    const {
        isLoading,
        loadingError,
        data,
        setData,
        readonly,
        trackChange,
    } = props;

    const selectUserResetPeriod = useCallback((resetPeriod: ResetPeriod) => {
        trackChange('User Reset Period');
        setData(state => ({...state, resetPeriod}));
    }, [setData, trackChange]);

    const changeNextResetDateChange = useCallback((nextResetDate: string) => {
        trackChange('Next User Reset Date');
        setData(state => ({...state, nextResetDate: nextResetDate || null}));
    }, [setData, trackChange]);

    const changeNoResetDate = useCallback((changeData: CheckBoxChangeData) => {
        trackChange('No Reset Date Required');
        const exceptUserReset = changeData.event.currentTarget.checked;
        setData(state => ({...state, exceptUserReset}));
    }, [setData, trackChange]);

    return (
        <Skeleton shown={isLoading} view={<UserResetSettingsSkeleton/>}>
            <ServerResponseHandler
                smallView={true}
                accessRight={ACCESS_RIGHTS.MICROSITES_SCREEN_VIEW as AccessRight}
                serverError={loadingError}>
                <Group direction="v">
                    <CheckBox
                        disabled={readonly}
                        label="No Reset Date Required (Not Recomended)"
                        checked={data.exceptUserReset}
                        onCheckBoxChange={changeNoResetDate}
                    />
                    <ValidatedControl name={NEXT_RESET_DATE_CONTROL}>
                        {() => (
                            <WidthItem width={300}>
                                <Label><RequiredMark label="Next User Reset Date"/></Label>
                                <DateInput
                                    id="next-reset-date-picker"
                                    clearable={true}
                                    selectedDate={data.nextResetDate || ''}
                                    onChange={changeNextResetDateChange}
                                    readonly={readonly}
                                />
                            </WidthItem>
                        )}
                    </ValidatedControl>
                    <WidthItem width={300}>
                        <Label>Reset Period</Label>
                        <ComboBox
                            onChange={selectUserResetPeriod}
                            disabled={readonly}
                            value={data.resetPeriod}>
                            {Object.values(ResetPeriods).map(item => (
                                <ComboboxItem key={item} caption={item + ' months'} value={item}/>
                            ))}
                        </ComboBox>
                    </WidthItem>
                </Group>
            </ServerResponseHandler>
        </Skeleton>
    );
};
